import React, { useEffect, useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification, Image } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppSelect } from '../../constants/appSelect';
import { newObjectKeys, formatDateVN, DEFAULT_THUMB } from '../../utils/utility';
import { deleteVideo, getVideos } from '../../services/video';
import { getLessons } from '../../services/lesson';
import { getCurriculums } from '../../services/curriculum';
import { getAllCategories } from '../../services/category';
import isEqual from 'lodash/isEqual';

const itemName = 'Video';
const createItemLink = '/cms/quan-ly-video/them-video';
const editItemLink = '/cms/quan-ly-video/sua-video';
const { Text } = Typography;

const VideoList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });

  // Initialize params from URL on first load
  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 20;
    const keyword = searchParams.get('keyword') || undefined;
    const statusStr = searchParams.get('status');
    const statusVal = statusStr !== null ? Number(statusStr) : -1;
    const lessonStr = searchParams.get('lesson_id');
    const lessonVal = lessonStr !== null ? Number(lessonStr) : -1;

    const next = { page, limit };
    if (keyword) Object.assign(next, { keyword });
    if (statusVal >= 0) Object.assign(next, { status: [statusVal] });
    if (lessonVal > 0) Object.assign(next, { lesson_id: lessonVal });

    setParams(next);
    formSearch.setFieldsValue({
      keyword: keyword,
      status: statusVal >= 0 ? statusVal : -1,
      lesson_id: lessonVal > 0 ? lessonVal : -1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push params to URL when they change
  useEffect(() => {
    if (!params) return;
    const sp = {
      page: String(params.page || 1),
      limit: String(params.limit || 20),
    };
    if (params.keyword) sp.keyword = params.keyword;
    if (Array.isArray(params.status) && params.status.length > 0 && params.status[0] >= 0) {
      sp.status = String(params.status[0]);
    }
    if (typeof params.lesson_id === 'number' && params.lesson_id > 0) {
      sp.lesson_id = String(params.lesson_id);
    }
    setSearchParams(sp, { replace: true });
  }, [params, setSearchParams]);

  const { data: dataVideos, isPending, refetch } = useQuery({ queryKey: ['dataVideos', params], queryFn: () => getVideos(params) });

  // Supporting data: lessons and curriculums (cached)
  const { data: lessonListRes } = useQuery({
    queryKey: ['allLessonsForVideoList'],
    queryFn: () => getLessons({ page: 1, limit: 10000, status: [1] }),
    staleTime: 1000 * 60 * 10,
  });
  const { data: curriculumListRes } = useQuery({
    queryKey: ['allCurriculums'],
    queryFn: () => getCurriculums({ page: 1, limit: 10000, status: [1] }),
    staleTime: 1000 * 60 * 10,
  });
  const { data: allCategoriesRes } = useQuery({
    queryKey: ['allCategoriesForVideoList'],
    queryFn: getAllCategories,
    staleTime: 1000 * 60 * 10,
  });
  const lessonMap = useMemo(() => {
    return (lessonListRes?.data || []).reduce((acc, cur) => {
      acc[cur?.id] = cur;
      return acc;
    }, {});
  }, [lessonListRes]);
  const curriculumMap = useMemo(() => {
    return (curriculumListRes?.data || []).reduce((acc, cur) => {
      acc[cur?.id] = cur?.title;
      return acc;
    }, {});
  }, [curriculumListRes]);

  // Build map: lesson id -> "Parent — Child — … - Program - Lesson"
  const lessonPathLabelMap = useMemo(() => {
    const lessons = Array.isArray(lessonListRes?.data) ? lessonListRes.data : [];
    const currs = Array.isArray(curriculumListRes?.data) ? curriculumListRes.data : [];
    const cats = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const curTitle = currs.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const curCat = currs.reduce((acc, c) => { acc[c.id] = c.category_id || 0; return acc; }, {});
    const parentMap = cats.reduce((acc, c) => { acc[c.id] = c.parent_id || 0; return acc; }, {});
    const titleMap = cats.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const cache = {};
    const toCatPath = (id) => {
      if (cache[id]) return cache[id];
      const parts = [];
      let cur = id; const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) { guard.add(cur); parts.unshift(titleMap[cur] || ''); cur = parentMap[cur] || 0; }
      cache[id] = parts.filter(Boolean).join(' — ');
      return cache[id];
    };
    const map = {};
    lessons.forEach((l) => {
      const cid = curCat[l.curriculum_id] || 0;
      const catPath = toCatPath(cid);
      const pTitle = curTitle[l.curriculum_id] || '';
      const lTitle = l.title || '';
      map[l.id] = `${catPath || '—'} - ${pTitle} - ${lTitle}`.trim();
    });
    return map;
  }, [lessonListRes, curriculumListRes, allCategoriesRes]);

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deleteVideo,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Xóa ${itemName} thành công!` });
        refetch();
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.keyword) Object.assign(temp, { keyword: values?.keyword });
    if (values?.status >= 0) Object.assign(temp, { status: [values?.status] });
    if (values?.lesson_id > 0) Object.assign(temp, { lesson_id: values?.lesson_id });

    if (!isEqual(temp, params)) {
      setParams(temp);
      return;
    }
    refetch();
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'id',
      key: 'stt',
      width: 70,
      fixed: 'left',
      align: 'center',
      ellipsis: true,
      onHeaderCell: () => ({ style: { maxWidth: 70 } }),
      onCell: () => ({ style: { maxWidth: 70, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }),
      render: (_, __, index) => (params?.page - 1) * params?.limit + index + 1,
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      render: (_, { title, id }) => (
        <Link to={`${editItemLink}/${id}`} className="text-[#1677FF]">
          {title}
        </Link>
      ),
      minWidth: 150,
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Bài học',
      dataIndex: 'lesson_id',
      key: 'lesson',
      render: (_, { lesson_id }) => {
        const text = lessonPathLabelMap[lesson_id] || '';
        return <span title={text}>{text}</span>;
      },
      minWidth: 260,
      width: 360,
    },
    
    {
      title: 'Link video',
      dataIndex: 'video_link',
      key: 'video_link',
      render: (v) => {
        const s = typeof v === 'string' ? v : '';
        const truncated = s.length > 60 ? s.slice(0, 60) + '...' : s;
        return <Text title={s}>{truncated}</Text>;
      },
      minWidth: 150,
    },
    
    {
      title: 'Thứ tự',
      dataIndex: 'order',
      key: 'order',
      width: 90,
      align: 'center',
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (_, { updated_at }) => <Space direction="vertical">{updated_at && <Text className="text-[#667085]">{formatDateVN(updated_at)}</Text>}</Space>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const st = typeof status === 'string' || typeof status === 'number' ? Number(status) : status;
        const meta = (AppSelect.STATUS || []).find((item) => item?.value === st);
        return (
          <Tag className="px-2 py-1" color={meta?.color}>
            {meta?.label}
          </Tag>
        );
      },
      width: 70,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`${editItemLink}/${record?.id}`)}>
            <EditOutlined />
          </Button>
          {record?.status !== 2 && (
            <Button type="text" onClick={() => mutateDelete(record?.id)}>
              <DeleteOutlined />
            </Button>
          )}
        </Space>
      ),
      width: 70,
      fixed: 'right',
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách {itemName}</h1>
        <Link to={createItemLink}>
          <Button type="primary">
            <PlusOutlined />
            Thêm {itemName}
          </Button>
        </Link>
      </div>

      <Form name="search_videos" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="keyword" label={<Text>Từ khóa</Text>}>
              <Input placeholder="Nhập từ khóa tìm kiếm..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...(AppSelect.STATUS || [])]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={18} xl={14}>
            <Form.Item name="lesson_id" label={<Text>Bài học</Text>} initialValue={-1}>
              <Select
                showSearch
                allowClear
                placeholder="Chọn bài học"
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[
                  { value: -1, label: 'Tất cả' },
                  ...([...(Array.isArray(lessonListRes?.data) ? lessonListRes.data : [])]
                    .map((l) => ({ value: l.id, label: lessonPathLabelMap[l.id] || l.title || '' }))
                    .sort((a, b) => (a.label || '').localeCompare(b.label || ''))),
                ]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params && Object.values(newObjectKeys(params, ['keyword', 'status', 'lesson_id'])).some((value) => value) && (
              <Button
                htmlType="button"
                onClick={() => {
                  formSearch.resetFields();
                  setParams({ page: 1, limit: params.limit });
                }}
              >
                <DeleteOutlined />
                Xóa tìm kiếm
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <TableCustom columns={columns} dataSource={dataVideos?.data ?? []} totalItem={dataVideos?.total ?? 0} loading={isPending} params={params} setParams={setParams} />
    </div>
  );
};

export default VideoList;
